import React, { useState, useMemo } from "react";
import { ChevronDown, Search, X, Menu } from "lucide-react";
import MarkdownParser from "../MarkdownParser";

const RightSidebar = ({
  userData,
  timeSaved,
  annotations = null,
  onCloseAnnotations,
  setIsContextDrawerOpen
}) => {
  const [timeSavedOpen, setTimeSavedOpen] = useState(false);
  const [discoverOpen, setDiscoverOpen] = useState(true);

  // Group annotations by publisher
  const groupedAnnotations = useMemo(() => {
    if (!annotations) return {};
    
    return annotations.reduce((groups, annotation) => {
      const publisherName = annotation.summary.newsletter.publisher.name;
      const sentDate = new Date(annotation.summary.newsletter.sent_at).toLocaleDateString();
      
      if (!groups[publisherName]) {
        groups[publisherName] = {
          date: sentDate,
          items: []
        };
      }
      
      groups[publisherName].items.push(annotation);
      return groups;
    }, {});
  }, [annotations]);

  const toggleSection = (section) => {
    if (section === "timeSaved") {
      setTimeSavedOpen(!timeSavedOpen);
    } else if (section === "discover") {
      setDiscoverOpen(!discoverOpen);
    }
  };

  const renderAnnotationContent = (annotation) => {
    if (!annotation.answer) return null;
    
    try {
      // Try to parse as JSON if it's a JSON string
      const parsedAnswer = typeof annotation.answer === 'string' 
        ? JSON.parse(annotation.answer) 
        : annotation.answer;
      
      if (typeof parsedAnswer === 'object') {
        return (
          <div>
            {Object.entries(parsedAnswer).map(([key, value], idx) => (
              <p key={idx} className="text-xs text-gray-700 mb-1">
                <span className="font-medium">{key}:</span> {value}
              </p>
            ))}
          </div>
        );
      }
    } catch (e) {
      // If it's not valid JSON, just display as text
    }
    
    // Default to displaying as text/markdown
    return <MarkdownParser markdown={annotation.answer} />;
  };

  const handleClose = () => {
    if (onCloseAnnotations) {
      onCloseAnnotations();
    }
    setIsContextDrawerOpen(false);
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex justify-between items-center p-4 border-b border-gray-200">
        <h2 className="font-semibold text-sm">
          {annotations ? "Annotations" : "Insights"}
        </h2>
        <div 
          className="text-gray-500 cursor-pointer hover:text-gray-700"
          onClick={handleClose}
        >
          <X size={24} />
        </div>
      </div>
      <div className="p-4 relative flex-grow overflow-auto">
        {annotations ? (
          <div className="mb-4 border border-gray-200 rounded-lg sticky top-0 bg-white z-10">
            <h2 className="font-semibold text-sm flex items-center justify-between p-4 border-b border-gray-200">
              <span>Annotations</span>
            </h2>
            <div className="max-h-[80vh] overflow-y-auto">
              {annotations.length === 0 ? (
                <div className="p-4 text-sm text-gray-500">No annotations found</div>
              ) : (
                Object.entries(groupedAnnotations).map(([publisherName, group], groupIndex) => (
                  <div key={groupIndex} className="border-b border-gray-200">
                    <div className="p-3 bg-gray-50 flex items-center justify-between">
                      <span className="font-medium text-sm text-gray-700">{publisherName}</span>
                      <span className="text-xs text-gray-500">{group.date}</span>
                    </div>
                    {group.items.map((annotation, index) => (
                      <div key={index} className="p-4 border-b border-gray-100 last:border-b-0">
                        <div className="mb-2">
                          <div className="text-sm font-medium text-gray-800 mb-1">
                            {annotation.question_text}
                          </div>
                          <div className="text-xs text-gray-500 mb-2">
                            {new Date(annotation.created_at).toLocaleDateString()} • {annotation.question_type}
                          </div>
                          <div className="text-xs text-gray-700">
                            {renderAnnotationContent(annotation)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ))
              )}
            </div>
          </div>
        ) : (
          <div className="text-center text-gray-500 p-4">
            <p>Select a summary with annotations to view them here.</p>
          </div>
        )}
        
        {/* Hidden content - kept for future use */}
        <div className="hidden">
          <div className="mb-4 border border-gray-200 rounded-lg sticky top-0 bg-white z-10">
            <h2
              className="font-semibold text-sm flex items-center justify-between cursor-pointer p-4"
              onClick={() => toggleSection("timeSaved")}
            >
              <span>Time Saved</span>
              <div className="flex items-center">
                <span className="text-blue-500 text-xs font-normal mr-2">
                  {timeSaved} min
                </span>
                <ChevronDown
                  size={16}
                  className={`text-gray-400 transform transition-transform duration-300 ${timeSavedOpen ? "rotate-180" : ""
                    }`}
                />
              </div>
            </h2>
            <div
              className={`overflow-hidden transition-all duration-300 ease-in-out ${timeSavedOpen ? "max-h-[1000px] opacity-100" : "max-h-0 opacity-0"
                }`}
            >
              <div className="bg-blue-50 p-4 border-t border-gray-200">
                <p className="text-sm font-medium text-gray-600 mb-1">
                  This Week
                </p>
                <div className="text-2xl font-bold text-blue-600 mb-2">
                  {timeSaved} minutes
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5 mb-2">
                  <div
                    className="bg-blue-600 h-2.5 rounded-full"
                    style={{
                      width: `${Math.min((timeSaved / 120) * 100, 100)}%`,
                    }}
                  ></div>
                </div>
                <div className="flex justify-between text-xs text-gray-600 mb-4">
                  <span>{timeSaved} min</span>
                  <span>Goal: 120 min</span>
                </div>
                <div className="space-y-2">
                  <p className="text-sm text-gray-600 flex justify-between">
                    <span>Total Time Saved:</span>
                    <span className="font-medium">{timeSaved} minutes</span>
                  </p>
                  <p className="text-sm text-gray-600 flex justify-between">
                    <span>Newsletters Summarized:</span>
                    <span className="font-medium">
                      {userData?.summaries_count || 0}
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="mb-4 border-gray-200 sticky top-0 bg-white z-10">
            <div className="border-t border-gray-200 pb-2 w-full" />
            <div className="flex items-center bg-gray-100 rounded p-2 mb-4">
              <Search className="text-gray-400 mr-2" size={16} />
              <input
                type="text"
                placeholder="Search links, social media, podcasts, ..."
                className="bg-transparent w-full text-sm"
              />
            </div>
            <div className="space-y-4">
              <div
                className="border-b pb-4 cursor-pointer hover:bg-gray-100 transition-colors duration-200 -mx-4 px-4"
                onClick={() => console.log("Clicked on Semrush incorporation")}
              >
                <h3 className="font-semibold mb-2 text-sm">
                  When was Semrush incorporated?
                </h3>
                <p className="text-xs text-gray-700">
                  Semrush was founded in 2008 by Oleg Shchegolev and Dmitry
                  Melnikov.
                </p>
                <p className="text-xs text-gray-700">
                  Initially started as a small group of SEO and IT specialists.
                </p>
                <p className="text-xs text-gray-700">
                  Officially incorporated as SEMrush Inc. in 2012 in Pennsylvania,
                  USA.
                </p>
              </div>
              <div
                className="border-b pb-4 cursor-pointer hover:bg-gray-100 transition-colors duration-200 -mx-4 px-4"
                onClick={() => console.log("Clicked on SEO trends")}
              >
                <h3 className="font-semibold mb-2 text-sm">
                  Top 10 trends for SEO keywords 2024
                </h3>
                <p className="text-xs text-gray-700">
                  1. Voice search optimization
                </p>
                <p className="text-xs text-gray-700">
                  2. AI-generated content keywords
                </p>
                <p className="text-xs text-gray-700">
                  3. Video content optimization
                </p>
                <p className="text-xs text-gray-700">4. Local SEO focus</p>
                <p className="text-xs text-gray-700">
                  5. Long-tail conversational keywords
                </p>
                {/* ... other trends */}
              </div>
              <div
                className="border-b pb-4 cursor-pointer hover:bg-gray-100 transition-colors duration-200 -mx-4 px-4"
                onClick={() => console.log("Clicked on SEO celebrities")}
              >
                <h3 className="font-semibold mb-2 text-sm">
                  Who is the biggest celebrity in SEO?
                </h3>
                <p className="text-xs text-gray-700">
                  While "celebrity" might be subjective in SEO, some notable
                  figures include:
                </p>
                <p className="text-xs text-gray-700">
                  - Rand Fishkin, founder of Moz and SparkToro
                </p>
                <p className="text-xs text-gray-700">
                  - Neil Patel, digital marketing entrepreneur and influencer
                </p>
                <p className="text-xs text-gray-700">
                  - Danny Sullivan, former editor of Search Engine Land, now with
                  Google
                </p>
              </div>
            </div>
          </div>
          <div className="mb-4 border border-gray-200 rounded-lg sticky top-0 bg-white z-10">
            <h2
              className="font-semibold text-sm flex items-center justify-between cursor-pointer p-4"
              onClick={() => toggleSection("discover")}
            >
              <span>Suggested Newsletters</span>
              <ChevronDown
                size={16}
                className={`text-gray-400 transform transition-transform duration-300 ${discoverOpen ? "rotate-180" : ""
                  }`}
              />
            </h2>
            <div
              className={`overflow-hidden transition-all duration-300 ${discoverOpen ? "max-h-[1000px] opacity-100" : "max-h-0 opacity-0"
                }`}
            >
              <div className="p-4 border-t border-gray-200">
                <ul className="space-y-2">
                  <li className="flex items-center justify-between">
                    <span className="text-sm">TechCrunch</span>
                    <button className="text-xs text-blue-500">Subscribe</button>
                  </li>
                  <li className="flex items-center justify-between">
                    <span className="text-sm">Morning Brew</span>
                    <button className="text-xs text-blue-500">Subscribe</button>
                  </li>
                  <li className="flex items-center justify-between">
                    <span className="text-sm">The Hustle</span>
                    <button className="text-xs text-blue-500">Subscribe</button>
                  </li>
                  <li className="flex items-center justify-between">
                    <span className="text-sm">TLDR</span>
                    <button className="text-xs text-blue-500">Subscribe</button>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RightSidebar;
